import React, { useEffect, useState, useCallback } from "react";
import MetaTags from "react-meta-tags";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Button,
  Table,
  Alert
} from "reactstrap";
import Loader from "../../components/Common/Loader";
import Select from "react-select";
import {
  getAgent,
  getCampaignFullList,
  getGestionesContactoRead,
  getReportsForTimeList
} from "../../helpers/backend";
import ModalManagementDetails from "../Whatsapp/components/ModalManagementDetails";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import "flatpickr/dist/l10n/es.js";
import "./Css/Report.css";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

const ReportForTime = () => {
  // Estados
  const [detallesModal, setDetallesModal] = useState(false);
  const [gestionDetallada, setGestionDetallada] = useState(null);
  const [filters, setFilters] = useState({
    fechaInicio: "",
    fechaFin: "",
    estado: "",
    agente: "",
    campaña: ""
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [showWarning, setShowWarning] = useState(false);
  const [selectedGestiones, setSelectedGestiones] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({
    agents: [],
    campaign: null,
    estado: null
  });

  // Obtener datos iniciales (agentes y campañas)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [agentsRes, campaignsRes] = await Promise.all([
          getAgent(),
          getCampaignFullList()
        ]);

        setAgents(agentsRes.data);
        setCampaigns(campaignsRes.data);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, []);

  // Manejar cambios de fecha
  const handleDateChange = useCallback((selectedDates) => {
    if (selectedDates.length === 2) {
      const [start, end] = selectedDates;
      setSelectedDate(selectedDates);
      setFilters((prev) => ({
        ...prev,
        fechaInicio: start.toISOString().split("T")[0],
        fechaFin: end.toISOString().split("T")[0]
      }));
    }
  }, []);

  // Manejar cambios en los selects
  const handleSelectChange = useCallback((field, selectedOption) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [field]: selectedOption
    }));

    const value = Array.isArray(selectedOption)
      ? selectedOption.map((opt) => opt.value).join(",")
      : selectedOption?.value || "";

    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Obtener reporte de intervalos
  const getReportData = useCallback(async () => {
    if (!filters.fechaInicio || !filters.fechaFin) {
      setShowWarning(true);
      return;
    }

    setLoading(true);
    setSelectedGestiones([]);

    try {
      const params = new URLSearchParams({
        fecha_inicio: filters.fechaInicio,
        fecha_fin: filters.fechaFin,
        estado: filters.estado,
        agents: filters.agente,
        campaign: filters.campaña,
        type: "WhatsApp"
      });

      const response = await getReportsForTimeList(`?${params.toString()}`);
      console.log("Datos recibidos:", response.data); // Para depuración
      setData(response.data);
    } catch (error) {
      console.error("Error fetching interval report data:", error);
    } finally {
      setLoading(false);
      setShowWarning(false);
    }
  }, [filters]);

  // Resetear búsqueda
  const resetSearch = useCallback(() => {
    setFilters({
      fechaInicio: "",
      fechaFin: "",
      estado: "",
      agente: "",
      campaña: ""
    });
    setSelectedDate(null);
    setSelectedOptions({
      agents: [],
      campaign: null,
      estado: null
    });
    setSelectedGestiones([]);
    setData({});
  }, []);

  const getChartData = useCallback(() => {
    if (!data.intervalos || typeof data.intervalos !== 'object') {
      return [];
    }

    // Definir el orden de los intervalos
    const intervalOrder = ['1-5', '6-10', '11-20', '21-30', '30+'];
    
    return intervalOrder
      .filter(interval => data.intervalos[interval] && data.intervalos[interval].contador > 0)
      .map(interval => ({
        intervalo: `${interval} min`,
        gestiones: data.intervalos[interval].contador,
        details: data.intervalos[interval].gestiones || []
      }));
  }, [data]);

  // Manejar el clic en una barra del gráfico
  const handleBarClick = (data) => {
    if (data?.activePayload?.[0]?.payload?.details) {
      setSelectedGestiones(data.activePayload[0].payload.details);
    }
  };

  // Manejar la apertura del modal con los detalles de la gestión
  const handleGestionDetallada = async (id) => {
    const response = await getGestionesContactoRead(id);
    setGestionDetallada(response.data);
    setDetallesModal(true);
  };

  // Datos formateados para el gráfico
  const chartData = getChartData();

  return (
    <div className="page-content report-for-time-container">
      <MetaTags>
        <title>Cuidad Limpia | Reporte por Intervalo</title>
      </MetaTags>

      <Container fluid>
        {/* Encabezado */}
        <div className="report-header">
          <h2 className="report-title">Reporte por intervalo de atención</h2>
          <p className="report-subtitle">
            Visualiza las gestiones por tiempo de atención
          </p>
        </div>

        {/* Modal de detalles */}
        {detallesModal && (
          <ModalManagementDetails
            isOpen={detallesModal}
            onCloseClick={() => setDetallesModal(false)}
            GestionDetallada={gestionDetallada}
          />
        )}

        {/* Panel de filtros */}
        <Card className="filter-card">
          <CardBody>
            <Row>
              <Col md={12} lg={3} className="mb-3">
                <label className="filter-label">Rango de fechas</label>
                <Flatpickr
                  className="form-control"
                  placeholder="Seleccione fechas"
                  options={{
                    mode: "range",
                    dateFormat: "Y-m-d",
                    locale: "es",
                    maxDate: new Date()
                  }}
                  onChange={handleDateChange}
                  value={selectedDate}
                />
              </Col>

              <Col md={12} lg={2} className="mb-3">
                <label className="filter-label">Estado</label>
                <Select
                  onChange={(opt) => handleSelectChange("estado", opt)}
                  options={[
                    { value: "", label: "Todos" },
                    { value: "EN_GESTION", label: "En Gestión" },
                    { value: "VENCIDO", label: "Vencido" },
                    { value: "FINALIZADO", label: "Finalizado" },
                    { value: "PENDIENTE", label: "Pendiente" }
                  ]}
                  placeholder="Estado"
                  value={selectedOptions.estado}
                  menuPosition="fixed"
                />
              </Col>

              <Col md={12} lg={3} className="mb-3">
                <label className="filter-label">Agentes</label>
                <Select
                  options={[
                    { label: "Todos", value: "" },
                    ...agents.map((agent) => ({
                      label: agent.get_full_name,
                      value: agent.id
                    }))
                  ]}
                  value={selectedOptions.agents}
                  onChange={(opt) => handleSelectChange("agente", opt)}
                  isMulti
                  placeholder="Seleccione agentes"
                  menuPosition="fixed"
                />
              </Col>

              <Col md={12} lg={2} className="mb-3">
                <label className="filter-label">Campañas</label>
                <Select
                  options={[
                    { label: "Todas", value: "" },
                    ...campaigns.map((campaign) => ({
                      label: campaign.name,
                      value: campaign.id
                    }))
                  ]}
                  onChange={(opt) => handleSelectChange("campaña", opt)}
                  value={selectedOptions.campaign}
                  menuPosition="fixed"
                  placeholder="Seleccione campaña"
                />
              </Col>

              <Col md={12} lg={2} className="d-flex align-items-end mb-3">
                <div className="d-flex w-100">
                  <Button
                    color="primary"
                    className="me-2 flex-grow-1"
                    onClick={getReportData}
                    disabled={loading}
                  >
                    {loading ? "Consultando..." : "Consultar"}
                  </Button>
                  <Button
                    outline
                    color="secondary"
                    className="flex-grow-1"
                    onClick={resetSearch}
                  >
                    Limpiar
                  </Button>
                </div>
              </Col>
            </Row>

            {showWarning && (
              <Alert color="warning" className="mt-2">
                Por favor, seleccione un rango de fechas para continuar.
              </Alert>
            )}
          </CardBody>
        </Card>

        {/* Resultados */}
        {loading ? (
          <Loader />
        ) : (
          Object.keys(data).length > 0 && (
            <>
              <Row className="mt-4">
                <Col lg={12}>
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="mb-3">Resumen general</h5>
                      <Row>
                        <Col md={4}>
                          <div className="summary-card">
                            <h6>Total gestiones</h6>
                            <p className="summary-value">
                              {data.total_gestiones || 0}
                            </p>
                          </div>
                        </Col>
                        <Col md={8}>
                          <div className="summary-card">
                            <h6>Tiempo promedio de atención</h6>
                            <p className="summary-value">
                              {data.promedio_tiempo_atencion
                                ? data.promedio_tiempo_atencion.split(".")[0]
                                : "00:00:00"}
                            </p>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col lg={selectedGestiones.length > 0 ? 7 : 12}>
                  <Card className="chart-card">
                    <CardBody>
                      <h5 className="chart-title">
                        Gestiones por intervalo de tiempo (minutos)
                      </h5>
                      <div className="chart-container">
                        {chartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                              data={chartData}
                              onClick={handleBarClick}
                              margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 60
                              }}
                            >
                              <XAxis
                                dataKey="intervalo"
                                tick={{ fontSize: 12 }}
                                angle={-45}
                                textAnchor="end"
                              />
                              <YAxis />
                              <Tooltip
                                contentStyle={{
                                  borderRadius: "8px",
                                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                                }}
                                formatter={(value) => [
                                  `${value} gestiones`,
                                  "Cantidad"
                                ]}
                                labelFormatter={(label) =>
                                  `Intervalo: ${label}`
                                }
                              />
                              <Bar
                                dataKey="gestiones"
                                name="Gestiones"
                                animationDuration={1500}
                              >
                                {chartData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="text-center py-5">
                            <h6>No hay datos para mostrar</h6>
                            <p>
                              No se encontraron gestiones en los intervalos
                              seleccionados
                            </p>
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                {selectedGestiones.length > 0 && (
                  <Col lg={5}>
                    <Card className="details-card">
                      <CardBody>
                        <h5 className="details-title">
                          Detalles para el intervalo seleccionado
                          <span className="badge bg-primary ms-2">
                            {selectedGestiones.length} gestiones
                          </span>
                        </h5>
                        <div className="details-table-container">
                          <Table hover className="details-table">
                            <thead>
                              <tr>
                                <th>Contacto</th>
                                <th>Estado</th>
                                <th>Fecha</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedGestiones.map((gestion) => (
                                <tr
                                  key={gestion.id}
                                  onClick={() =>
                                    handleGestionDetallada(gestion.id)
                                  }
                                  className="clickable-row"
                                >
                                  <td>{gestion.contacto}</td>
                                  <td>
                                    <span
                                      className={`status-badge ${gestion.estado
                                        .toLowerCase()
                                        .replace(" ", "-")}`}
                                    >
                                      {gestion.estado}
                                    </span>
                                  </td>
                                  <td>
                                    {new Date(
                                      gestion.created_date
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </Row>
            </>
          )
        )}
      </Container>
    </div>
  );
};

export default ReportForTime;
